import { useQuery } from "@apollo/client/react"
import { Link, useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { dashboardPath, journeyLogEditPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import wordmarkUrl from "~/images/wp-mascot-wordmark.png"
import { SiteHeader } from "~/layouts/site-header"
import { Badge } from "~/ui/badge"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"

const profileQuery = gql(/* GraphQL */ `
  query PublicProfile($id: String!) {
    user(id: $id) {
      id
      firstName
      publicDisplayName
      bio
      avatarThumbUrl
      publicMemberSince
      publicPuzzlesLoggedCount
      publicPuzzlesRegisteredCount
      publicDistinctStatesVisited
      publicJourneyLogs {
        id
        completedOn
        city
        state
        comment
        photoThumbUrl
        authorDisplayName
        viewerIsAuthor
        puzzle {
          id
          wpId
          title
          brand
          pieceCount
          photoThumbUrl
        }
      }
      publicRegisteredPuzzles {
        id
        wpId
        title
        brand
        pieceCount
        homeCity
        homeState
        photoThumbUrl
        journeyLogCount
      }
    }
  }
`)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })

const formatMemberSince = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long" })

const StatTile = ({ label, value }: { label: string; value: number | string }) => (
  <div className="rounded-lg border bg-stone-50 px-4 py-3">
    <div className="text-2xl font-semibold">{value}</div>
    <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
  </div>
)

export const PublicProfileScreen = () => {
  const { userId } = useParams<{ userId: string }>()
  const { viewer } = useViewerMaybe()
  const { data, loading, error } = useQuery(profileQuery, {
    variables: { id: userId ?? "" },
    skip: !userId,
  })

  const user = data?.user
  useDocumentTitle(user ? `${user.publicDisplayName}'s puzzles` : userId ? "Profile" : null)

  if (loading && !data) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />
  if (!user) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-2xl font-semibold">Profile not found</h1>
        <p className="mt-2 text-gray-600">
          We couldn&apos;t find a wandering puzzler at that link.
        </p>
        <Link to="/" className="mt-6 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  return (
    <>
      <SiteHeader />
      <header className="bg-mascot-blue-pale text-stone-900">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-5 sm:gap-6 sm:py-6">
          <Link to="/" className="block shrink-0">
            <img
              src={wordmarkUrl}
              alt="Wandering Puzzles"
              className="h-16 w-16 rounded-full bg-white object-cover sm:h-20 sm:w-20"
            />
          </Link>
          <div>
            <h2 className="font-display text-xl font-semibold leading-tight sm:text-2xl">
              A Wandering Puzzler.
            </h2>
            <p className="mt-1 text-sm leading-snug text-stone-700 sm:text-base">
              These are the puzzles {user.publicDisplayName} has completed or added to Wandering
              Puzzles, a place where solved puzzles get passed on, scanned, and trace a journey from
              one table to the next.
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        {viewer ? (
          <Link
            to={dashboardPath({})}
            className="mb-6 inline-block text-sm text-stone-500 hover:text-stone-800"
          >
            ← Back to dashboard
          </Link>
        ) : null}
        <section className="flex flex-wrap items-center gap-4 sm:gap-6">
          {user.avatarThumbUrl ? (
            <img
              src={user.avatarThumbUrl}
              alt=""
              className="h-20 w-20 shrink-0 rounded-full bg-stone-100 object-cover sm:h-24 sm:w-24"
            />
          ) : (
            <div
              aria-hidden
              className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-mountain text-2xl font-semibold text-white sm:h-24 sm:w-24"
            >
              {user.firstName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500">Wandering puzzler</p>
            <h1 className="text-2xl font-semibold sm:text-3xl">{user.publicDisplayName}</h1>
            <p className="mt-1 text-sm text-gray-600">
              Solving since {formatMemberSince(user.publicMemberSince)}
            </p>
          </div>
        </section>

        {user.bio ? (
          <p className="mt-5 max-w-2xl whitespace-pre-line text-base leading-relaxed text-gray-700">
            {user.bio}
          </p>
        ) : null}

        <section className="mt-8 grid grid-cols-3 gap-3">
          <StatTile label="Puzzles logged" value={user.publicPuzzlesLoggedCount} />
          <StatTile label="Puzzles registered" value={user.publicPuzzlesRegisteredCount} />
          <StatTile label="States visited" value={user.publicDistinctStatesVisited} />
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">
            {user.firstName}&apos;s puzzle log
          </h2>
          {user.publicJourneyLogs.length === 0 ? (
            <Card className="mt-4">
              <CardContent className="py-12 text-center text-gray-500">
                No public stops yet.
              </CardContent>
            </Card>
          ) : (
            <ul className="mt-4 space-y-6">
              {user.publicJourneyLogs.map((log) => {
                const place = [log.city, log.state].filter(Boolean).join(", ")
                return (
                  <li key={log.id}>
                    <Card className="overflow-hidden">
                      {log.photoThumbUrl ? (
                        <div className="bg-stone-100">
                          <img
                            src={log.photoThumbUrl}
                            alt=""
                            className="max-h-[28rem] w-full object-contain"
                          />
                        </div>
                      ) : null}
                      <CardContent className="space-y-4 py-5 sm:py-6">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="text-sm text-gray-500">
                            {formatDate(log.completedOn)}
                            {place ? ` · ${place}` : null}
                          </div>
                          {log.viewerIsAuthor ? (
                            <Link
                              to={journeyLogEditPath({ id: log.id })}
                              className="text-sm text-mountain hover:underline"
                            >
                              Edit
                            </Link>
                          ) : null}
                        </div>
                        {log.comment ? (
                          <p className="whitespace-pre-line text-base leading-relaxed text-gray-800 sm:text-lg">
                            {log.comment}
                          </p>
                        ) : null}
                        <Link
                          to={`/p/${log.puzzle.wpId}`}
                          className="flex items-center gap-3 rounded-md border border-stone-200 bg-stone-50 p-3 transition hover:border-stone-300 hover:bg-stone-100"
                        >
                          {log.puzzle.photoThumbUrl ? (
                            <img
                              src={log.puzzle.photoThumbUrl}
                              alt=""
                              className="h-14 w-14 shrink-0 rounded bg-white object-contain"
                            />
                          ) : (
                            <div className="h-14 w-14 shrink-0 rounded bg-white" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium text-gray-900">
                              {log.puzzle.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {log.puzzle.brand} · {log.puzzle.pieceCount.toLocaleString()} pieces
                            </div>
                          </div>
                          <span className="shrink-0 text-sm text-mountain">→</span>
                        </Link>
                      </CardContent>
                    </Card>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold">
            Puzzles {user.publicDisplayName} has added
          </h2>
          {user.publicRegisteredPuzzles.length === 0 ? (
            <Card className="mt-4">
              <CardContent className="py-12 text-center text-gray-500">
                Hasn&apos;t registered any puzzles yet.
              </CardContent>
            </Card>
          ) : (
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {user.publicRegisteredPuzzles.map((puzzle) => (
                <li key={puzzle.id}>
                  <Card>
                    <CardContent className="flex gap-3 py-4">
                      <Link to={`/p/${puzzle.wpId}`} className="shrink-0">
                        {puzzle.photoThumbUrl ? (
                          <img
                            src={puzzle.photoThumbUrl}
                            alt={puzzle.title}
                            className="h-16 w-16 rounded bg-stone-100 object-contain"
                          />
                        ) : (
                          <div className="h-16 w-16 rounded bg-gray-100" />
                        )}
                      </Link>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/p/${puzzle.wpId}`}
                            className="truncate font-medium hover:underline"
                          >
                            {puzzle.title}
                          </Link>
                          <Badge variant="secondary" className="font-mono text-[10px]">
                            {puzzle.wpId}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500">
                          {puzzle.brand} · {puzzle.pieceCount.toLocaleString()} pieces
                        </div>
                        <div className="mt-1 text-sm text-gray-700">
                          {puzzle.journeyLogCount} {puzzle.journeyLogCount === 1 ? "log" : "logs"}
                          {puzzle.homeCity || puzzle.homeState
                            ? ` · started in ${[puzzle.homeCity, puzzle.homeState].filter(Boolean).join(", ")}`
                            : null}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </>
  )
}
